import {
  Button,
  H5,
  InputCheckbox,
  InputCurrency,
} from "@sellar-io/components";
import { formatCurrency } from "@sellar-io/common";

import { ReactElement } from "react";
import { ItemTypeImage, SpinnerLegacy } from "../../../components";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { usePriceFormat } from "../../../hooks/usePriceFormat";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import type { ProductListResponseType } from "@sellar-io/api-v2/types";
import { useProductPriceTierCreate } from "../../../hooks/api/v2/productPriceTier/useProductPriceTierCreate";
import { useProductPriceTierUpdate } from "../../../hooks/api/v2/productPriceTier/useProductPriceTierUpdate";
import { useProductPriceTierDelete } from "../../../hooks/api/v2/productPriceTier/useProductPriceTierDelete";
import { PRICE_TIER } from "../../../constants/routes";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";

const isNumber = (num: unknown): num is number => {
  return Number.isInteger(num);
};

const ProductPriceTierItemContent = ({
  product,
  currency,
  currentPriceApiFormat,
  updatedPriceFormat,
  onUpdateClicked,
  onDiscountableToggled,
  onPriceChanged,
}: {
  product: ProductListResponseType[number];
  currency?: string;
  currentPriceApiFormat: number | null;
  updatedPriceFormat: ReturnType<typeof usePriceFormat>;
  onUpdateClicked: () => void;
  onDiscountableToggled?: (checked: boolean) => void;
  onPriceChanged: (price: string) => void;
}) => {
  const productPriceTier = product.ProductPriceTier;

  return (
    <div>
      <H5 className={"my-4 flex items-center gap-2"}>
        <ItemTypeImage
          product={{
            containerType: product.containerType ?? null,
            type: product.type,
          }}
        />
        {product.name}
      </H5>
      {currentPriceApiFormat !== null && onDiscountableToggled ? (
        <div>
          <p className="font-bold text-primary">
            {formatCurrency(currentPriceApiFormat, currency)}
          </p>

          <InputContainer className="border-none">
            <InputCheckbox
              required={false}
              checked={productPriceTier?.discountable ?? true}
              onChange={(e) => onDiscountableToggled(e.target.checked)}
            />
            <InputLabel>Discountable</InputLabel>
          </InputContainer>
        </div>
      ) : null}

      <div className={"flex items-center justify-end gap-1"}>
        <InputContainer>
          <InputCurrency
            showPrefix
            allowNegative
            className={"max-w-[110px]"}
            required={false}
            placeholder="No price"
            value={updatedPriceFormat.inputFormat}
            onChange={(e) => onPriceChanged(e.target.value)}
            min={0}
          />
        </InputContainer>
        <Button
          size={"small"}
          disabled={currentPriceApiFormat === updatedPriceFormat.apiFormat}
          onClick={onUpdateClicked}
        >
          Update
        </Button>
      </div>
    </div>
  );
};

const EditItemProductPriceTier = ({
  product,
  currentPriceFormat,
  updatedPriceFormat,
}: {
  product: ProductListResponseType[number] & {
    ProductPriceTier: { id: number; discountable: boolean };
    ProductAvailabilityTier: { id: number; active: boolean } | null;
  };
  currentPriceFormat: ReturnType<typeof usePriceFormat>;
  updatedPriceFormat: ReturnType<typeof usePriceFormat>;
}) => {
  const { business } = useSellarCurrentBusiness();
  const productPriceTierDelete = useProductPriceTierDelete(
    product.ProductPriceTier.id,
  );
  const productPriceTierUpdate = useProductPriceTierUpdate(
    product.ProductPriceTier.id,
  );
  const isLoading =
    productPriceTierDelete.isPending || productPriceTierUpdate.isPending;

  if (isLoading) {
    return <SpinnerLegacy />;
  }

  return (
    <ProductPriceTierItemContent
      product={product}
      currency={business?.Supplier?.currency}
      currentPriceApiFormat={currentPriceFormat.apiFormat}
      updatedPriceFormat={updatedPriceFormat}
      onPriceChanged={(price) => {
        updatedPriceFormat.setInputFormat(price);
      }}
      onUpdateClicked={() => {
        if (updatedPriceFormat.apiFormat === null) {
          return productPriceTierDelete.mutateAsync().then(() => {
            currentPriceFormat.setApiFormat(null);
          });
        }
        return productPriceTierUpdate.mutate({
          price: updatedPriceFormat.apiFormat,
        });
      }}
      onDiscountableToggled={(discountable) => {
        productPriceTierUpdate.mutate({ discountable });
      }}
    />
  );
};

const CreateItemProductPriceTier = ({
  product,
  currentPriceFormat,
  updatedPriceFormat,
}: {
  product: ProductListResponseType[number];
  currentPriceFormat: ReturnType<typeof usePriceFormat>;
  updatedPriceFormat: ReturnType<typeof usePriceFormat>;
}) => {
  const priceTierId = useParamAsNumber(PRICE_TIER, "priceTierId");

  const { business } = useSellarCurrentBusiness();
  const productPriceTierCreate = useProductPriceTierCreate();
  const updatePriceApiFormat = updatedPriceFormat.apiFormat;
  const isLoading = productPriceTierCreate.isPending;

  if (isLoading) {
    return <SpinnerLegacy />;
  }

  return (
    <ProductPriceTierItemContent
      product={product}
      currency={business?.Supplier?.currency}
      currentPriceApiFormat={currentPriceFormat.apiFormat}
      updatedPriceFormat={updatedPriceFormat}
      onPriceChanged={(price) => {
        updatedPriceFormat.setInputFormat(price);
      }}
      onUpdateClicked={() => {
        if (isNumber(updatePriceApiFormat)) {
          productPriceTierCreate.mutateAsync({
            ProductId: product.id,
            PriceTierId: priceTierId,
            basePrice: null,
            price: updatePriceApiFormat,
            discountable: product.type !== "badge",
            active: true,
          });
        }
      }}
    />
  );
};

export const PriceTierItem = ({
  product,
}: {
  product: ProductListResponseType[number];
}): ReactElement => {
  const currentPriceFormat = usePriceFormat(product.ProductPriceTier?.price);
  const updatedPriceFormat = usePriceFormat(product.ProductPriceTier?.price);
  const productPriceTier = product.ProductPriceTier;
  const productAvailabilityTier = product.ProductAvailabilityTiers[0];

  return productPriceTier && isNumber(currentPriceFormat.apiFormat) ? (
    <EditItemProductPriceTier
      product={{
        ...product,
        ProductPriceTier: productPriceTier,
        ProductAvailabilityTier: productAvailabilityTier ?? null,
      }}
      currentPriceFormat={currentPriceFormat}
      updatedPriceFormat={updatedPriceFormat}
    />
  ) : (
    <CreateItemProductPriceTier
      product={product}
      currentPriceFormat={currentPriceFormat}
      updatedPriceFormat={updatedPriceFormat}
    />
  );
};
